// @ts-ignore — highcharts 6.2.0 has no bundled types
import Highcharts from 'highcharts';
// @ts-ignore
import HighchartsReact from 'highcharts-react-official';
import { useId, useState } from 'react';
import { useIntl } from 'react-intl';
import { Tag } from 'sonata-react-components';

import type { DayKey } from 'sonata-static-storage/lib/entities/footfallReport/footfallReport.types';
import type { DayPopularTimes, PopularTimesData } from '@/types/footfall';

import './popularTimes.scss';

const DAYS_ORDER: DayKey[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const DAY_LABELS: Record<DayKey, string> = {
  friday: 'Fri',
  monday: 'Mon',
  saturday: 'Sat',
  sunday: 'Sun',
  thursday: 'Thu',
  tuesday: 'Tue',
  wednesday: 'Wed'
};

const JS_DAY_TO_KEY: Record<number, DayKey> = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday'
};

const BAR_COLOR_NORMAL = '#dde9fd';
const BAR_COLOR_HIGHLIGHTED = '#c2d3f1';
const HIGHLIGHT_THRESHOLD = 0.6;
const PEAK_SCORE_THRESHOLD = 0.65;

type Props = {
  data: PopularTimesData;
};

function buildChartData(dayData: DayPopularTimes | undefined): Highcharts.PointOptionsObject[] {
  const hours = dayData?.hours ?? [];
  const max = Math.max(...hours.map(h => h.score), 1);
  const threshold = max * HIGHLIGHT_THRESHOLD;
  return hours.map(h => ({
    color: h.score >= threshold ? BAR_COLOR_HIGHLIGHTED : BAR_COLOR_NORMAL,
    y: h.score
  }));
}

function buildHourCategories(dayData: DayPopularTimes | undefined): string[] {
  return (dayData?.hours ?? []).map(h => `${h.hour}:00h`);
}

function buildPeakRange(dayData: DayPopularTimes | undefined): { start: number; end: number } | null {
  const hours = dayData?.hours ?? [];
  const max = Math.max(...hours.map(h => h.score), 1);
  const peakHours = hours.filter(h => h.score >= max * PEAK_SCORE_THRESHOLD);
  if (peakHours.length === 0) {
    return null;
  }
  return { end: peakHours[peakHours.length - 1].hour, start: peakHours[0].hour };
}

type OpenHoursGroup = { dayRange: string; schedule: string };

function buildOpenHoursGroups(days: DayPopularTimes[]): OpenHoursGroup[] {
  const groups: { days: string[]; schedule: string }[] = [];

  for (const dayKey of DAYS_ORDER) {
    const dayData = days.find(d => d.day === dayKey);
    const schedule =
      dayData?.openHour !== null && dayData?.openHour !== undefined && dayData?.closeHour !== null
        ? `${dayData.openHour}:00-${dayData.closeHour}:00`
        : 'Closed';

    const last = groups[groups.length - 1];
    if (last && last.schedule === schedule) {
      last.days.push(DAY_LABELS[dayKey]);
    } else {
      groups.push({ days: [DAY_LABELS[dayKey]], schedule });
    }
  }

  return groups.map(g => ({
    dayRange: g.days.length > 1 ? `${g.days[0]}-${g.days[g.days.length - 1]}` : g.days[0],
    schedule: g.schedule
  }));
}

export default function PopularTimes({ data }: Props) {
  const { formatMessage } = useIntl();
  const dwellTagId = useId();
  const todayDayKey = JS_DAY_TO_KEY[new Date().getDay()];
  const [selectedDay, setSelectedDay] = useState<DayKey>(todayDayKey);

  const selectedDayData = data.days.find(d => d.day === selectedDay);
  const chartData = buildChartData(selectedDayData);
  const hourCategories = buildHourCategories(selectedDayData);
  const peakRange = buildPeakRange(selectedDayData);
  const openHoursGroups = buildOpenHoursGroups(data.days);
  const isClosed = (selectedDayData?.hours ?? []).length === 0;

  const dwellTimeText =
    data.dwellTimeMin !== null && data.dwellTimeMax !== null
      ? `${Math.round(data.dwellTimeMin)}-${Math.round(data.dwellTimeMax)} min`
      : null;

  const chartOptions = {
    chart: {
      animation: false,
      backgroundColor: 'transparent',
      height: 170,
      margin: [10, 0, 24, 0],
      type: 'column'
    },
    credits: { enabled: false },
    legend: { enabled: false },
    plotOptions: {
      column: {
        borderRadius: 3,
        borderWidth: 0,
        groupPadding: 0.05,
        pointPadding: 0.02
      },
      series: {
        states: {
          hover: {
            brightness: -0.1
          }
        }
      }
    },
    series: [
      {
        data: chartData,
        name: formatMessage({ defaultMessage: 'Visits', id: 'driveToStore.footfallReport.popularTimes.visits' }),
        type: 'column'
      }
    ],
    title: { text: '' },
    tooltip: {
      backgroundColor: 'var(--color-sonata-secondary-white, #FFF)',
      // biome-ignore lint/suspicious/noExplicitAny: highcharts 6.2.0 has no bundled types
      formatter(this: any): string {
        return `<span style="color:var(--color-sonata-secondary-700, #000);">${this.x}: ${this.y}</span>`;
      },
      style: { color: 'var(--color-sonata-secondary-700, #000);' },
      useHTML: true
    },
    xAxis: {
      categories: hourCategories,
      labels: {
        rotation: 0,
        step: Math.ceil(hourCategories.length / 8),
        style: {
          color: 'var(--color-sonata-primary-600)',
          font: 'var(--font-info-600-regular)'
        }
      },
      lineWidth: 0,
      tickLength: 0
    },
    yAxis: {
      gridLineWidth: 0,
      labels: { enabled: false },
      title: { text: null }
    }
  };

  return (
    <div className="popularTimes">
      <div className="popularTimes__header">
        <h3 className="popularTimes__title">
          {formatMessage({ defaultMessage: 'Popular times', id: 'driveToStore.footfallReport.popularTimes.title' })}
        </h3>
        {dwellTimeText !== null ? (
          <Tag icon={{ webFont: 'fa-regular fa-clock' }} id={dwellTagId} status="category">
            {`${formatMessage({ defaultMessage: 'Dwell time:', id: 'driveToStore.footfallReport.popularTimes.dwellTime' })} ${dwellTimeText}`}
          </Tag>
        ) : (
          <Tag icon={{ webFont: 'fa-regular fa-clock' }} id={dwellTagId} status="category">
            {`${formatMessage({ defaultMessage: 'Dwell time:', id: 'driveToStore.footfallReport.popularTimes.dwellTime' })} —`}
          </Tag>
        )}
      </div>{' '}
      {peakRange !== null && (
        <p className="popularTimes__peakInfo">
          <span className="popularTimes__infoLabel">
            {formatMessage({
              defaultMessage: 'Peak time range:',
              id: 'driveToStore.footfallReport.popularTimes.peakTimeRange'
            })}
          </span>
          {` ${peakRange.start}:00h - ${peakRange.end}:00h`}
        </p>
      )}
      <p className="popularTimes__openInfo">
        <span className="popularTimes__infoLabel">
          {`${formatMessage({ defaultMessage: 'Open:', id: 'driveToStore.footfallReport.popularTimes.open' })} `}
        </span>
        {openHoursGroups.map((g, i) => (
          <span key={g.dayRange}>
            {i > 0 && ', '}
            <span className="popularTimes__openDay">{g.dayRange}</span>
            {`: ${g.schedule}`}
          </span>
        ))}
      </p>
      <div className="popularTimes__chart">
        {isClosed ? (
          <div className="popularTimes__closedMessage">
            {formatMessage({ defaultMessage: 'Closed', id: 'driveToStore.footfallReport.popularTimes.closed' })}
          </div>
        ) : (
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        )}
      </div>
      <div className="popularTimes__dayLabels">
        {DAYS_ORDER.map(day => {
          const isToday = day === todayDayKey;
          const isSelected = day === selectedDay;
          const dayRecord = data.days.find(d => d.day === day);
          const isClosed = dayRecord?.openHour === null;

          const classNames = [
            'popularTimes__dayLabel',
            isSelected && 'popularTimes__dayLabel--selected',
            isToday && 'popularTimes__dayLabel--today',
            isClosed && 'popularTimes__dayLabel--closed'
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button className={classNames} key={day} onClick={() => setSelectedDay(day)} type="button">
              <span className="popularTimes__dayName">
                {isToday && <span className="popularTimes__todayDot" />}
                {DAY_LABELS[day]}
              </span>
              {isToday && (
                <span className="popularTimes__todayText">
                  {formatMessage({ defaultMessage: 'Today', id: 'driveToStore.footfallReport.popularTimes.today' })}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
